







































































































import { Component, Prop, Watch, Vue } from 'vue-property-decorator';
import {
    GroupChatViewObject,
    ChatMessageViewObject,
    ReactionType,
    ChatMessageReactionRequest,
    ChatMessageRequest,
    GroupChatMessageRequest
} from '@/types/apiServerContract';
import { ValidationObserver } from 'vee-validate/dist/types/components/Observer';
import ChatService from '@/services/chat/chat.service';
import { ChatModule } from '@/store/modules/chat/chat.store';
import Reactions from '@/components/frontpage/reactions/Reactions.vue';
import urlHelperService from '@/services/url/urlHelper.service';
import scrollService from '@/core/scroll/scroll.service';
import clipboardService from '@/core/clipboard/clipboard.service';

@Component({
    components: { Reactions }
})
export default class GroupChatMessageBoard extends Vue {
	@Prop({ required: true }) chat!: GroupChatViewObject;

	messageCounter: number = 20;
	totalMessages: number = 0;
	message: string = '';
	page: number = 0;
	loaded: boolean = false;
	loading: boolean = true;
	allFetched: boolean = false;
	showNewMessageBanner: boolean = false;

	isMemberOnline(member: ChatMessageViewObject) {
	    return this.chat.members.find(x => x.author === member.author && x.online) != null;
	}

	get messages(): ChatMessageViewObject[] {
	    return ChatModule.messages.filter(x => x.groupId === this.chat.id).sort((a, b) => (a.created < b.created ? -1 : 1));
	}

	isPreviousMessageSameAuthor(index: number) {
	    return index > 0 && this.messages[index].author === this.messages[index - 1].author;
	}

	async submit() {
	    await ChatService.connection.invoke('SendMessage', {
	        chatId: this.chat.id,
	        message: this.message.trim()
	    } as GroupChatMessageRequest);

	    this.reset();
	}

	async markMessageAsRead(messageId: string) {
	    await ChatService.connection.invoke('MarkMessageAsRead', {
	        messageId
	    } as ChatMessageRequest);
	}

	async reacted(data: { type: ReactionType; active: boolean; entityId: string }) {
	    await ChatService.connection.invoke('ChatMessageReaction', {
	        chatId: this.chat.id,
	        active: data.active,
	        messageId: data.entityId,
	        type: data.type
	    } as ChatMessageReactionRequest);
	}

	reset() {
	    this.message = '';
	    this.$refs.validator.reset();
	}

	public $refs!: {
		validator: InstanceType<typeof ValidationObserver>;
		history: HTMLElement;
	};

	scrollToBottom(quick: boolean = false) {
	    const activeMessageId = urlHelperService.getQueryParam<string>('message');

	    setTimeout(() => {
	        const history = this.$refs.history;
	        if (activeMessageId) {
	            const activeMessage = document.getElementById(activeMessageId);
	            if (activeMessage) {
	                scrollService.scrollToElement(activeMessage, -100, 0, history);
	            } else {
	                history.scrollTo({
	                    top: history.scrollHeight - history.clientHeight,
	                    behavior: quick ? 'auto' : 'smooth'
	                });
	            }
	        } else {
	            history.scrollTo({
	                top: history.scrollHeight - history.clientHeight,
	                behavior: quick ? 'auto' : 'smooth'
	            });
	        }
	    }, 25);
	}

	async getMessagesForChat() {
	    if (this.loading || this.allFetched) return;

	    this.clearActiveMessage();

	    this.loading = true;
	    const previousHeight = this.$refs.history.scrollHeight;

	    const activeMessageId = urlHelperService.getQueryParam<string>('message');

	    setTimeout(async() => {
	        try {
	            const result = await ChatModule.GetMessagesForChat({
	                chatId: this.chat.id,
	                page: this.page,
	                requestedMessageId: activeMessageId
	            });
	            this.page = result.pageNumber;

	            if (result.messages.length < 20 || this.messages.length >= this.totalMessages) {
	                this.allFetched = true;
	            }
	        } finally {
	            this.loading = false;

	            const newHeight = this.$refs.history.scrollHeight;
	            this.$refs.history.scrollTop = newHeight - previousHeight;
	        }
	    }, 1000);
	}

	async init() {
	    if (this.loaded) return;

	    ChatModule.ClearMessages();

	    const activeMessageId = urlHelperService.getQueryParam<string>('message');

	    try {
	        const result = await ChatModule.GetMessagesForChat({
	            chatId: this.chat.id,
	            page: this.page,
	            requestedMessageId: activeMessageId
	        });
	        this.totalMessages = result.total;
	        this.page = result.pageNumber;

	        if (result.messages.length < 20 || this.messages.length >= this.totalMessages) {
	            this.allFetched = true;
	        }
	    } finally {
	        this.scrollToBottom(true);

	        this.loading = false;

	        setTimeout(() => {
	            this.loaded = true;
	        }, 1000);
	    }
	}

	isMethodHighlited(messageId: string) {
	    const activeMessageId = urlHelperService.getQueryParam<string>('message');
	    return activeMessageId != null && activeMessageId === messageId;
	}

	isAtBottomOfChatWindow() {
	    const history = this.$refs.history;
	    return history.scrollHeight - history.clientHeight === history.scrollTop;
	}

	reachedBottomOfChatWindow() {
	    this.showNewMessageBanner = false;
	}

	scrolledUp() {
	    // this.isAtBottom = false;
	}

	@Watch('messages')
	onMessageChange(messages: ChatMessageViewObject[], old: ChatMessageViewObject[]) {
	    if (!this.loading && this.isAtBottomOfChatWindow()) {
	        this.scrollToBottom();
	    } else if (!this.loading) {
	        this.showNewMessageBanner = true;
	    }

	    if (!this.loading && messages.length > old.length) {
	        const newMessage = messages[messages.length - 1];
	        if (newMessage.author !== this.$user.id) {
	            this.markMessageAsRead(newMessage.id);
	        }
	    }
	}

	clearActiveMessage() {
	    const activeMessageId = urlHelperService.getQueryParam<string>('message');
	    if (activeMessageId) {
	        urlHelperService.removeQueryParam('message', activeMessageId);

	        const activeMessage = document.getElementById(activeMessageId);
	        if (activeMessage) {
	            var messages = Array.from(activeMessage.getElementsByClassName('active'));
	            messages.forEach(element => {
	                element.classList.remove('active');
	            });
	        }

	        this.$refs.history.removeEventListener('click', this.clearActiveMessage);
	    }
	}

	copyLink(message: ChatMessageViewObject) {
	    const url = urlHelperService.getBaseUrl() + '?chat=' + message.groupId + '&message=' + message.id;
	    if (clipboardService.copyToClipboard(url)) {
	        this.$bvToast.toast('Message link copied successfully', {
	            title: 'Success',
	            variant: 'success'
	        });
	    }
	}

	mounted() {
	    this.init();

	    this.$refs.history.addEventListener('click', this.clearActiveMessage);
	}

	beforeDestroy() {
	    this.$refs.history.removeEventListener('click', this.clearActiveMessage);
	}
}
